import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Comment } from 'services/comments/types';
import type { Event } from 'services/events/types';
import { getApiBaseUrl } from 'utils/user';
import { taskApiError } from './apiError';
import type { Task, TaskListQuery, TaskListResponse } from './types';

export const apiTasks = createApi({
  reducerPath: 'tasksApi',
  tagTypes: ['Task', 'Comment', 'Event'],
  baseQuery: fetchBaseQuery({
    baseUrl: `${getApiBaseUrl()}/`,
    credentials: 'include',
    prepareHeaders: (headers) => {
      headers.set('X-Client', 'web');
      return headers;
    },
  }),
  endpoints: builder => ({
    getTasks: builder.query<TaskListResponse, TaskListQuery | void>({
      query: (arg) => {
        const { filter, limit, offset, sort } = arg ?? {};
        return {
          url: 'tasks',
          params: {
            ...(filter != null && { filter: JSON.stringify(filter) }),
            ...(limit != null && { limit }),
            ...(offset != null && { offset }),
            ...(sort != null && { sort: JSON.stringify(sort) }),
          },
        };
      },
      transformResponse: (data: TaskListResponse) => data,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: ['Task'],
    }),
    getTask: builder.query<Task, string>({
      query: id => `tasks/${id}`,
      transformResponse: (data: { task: Task }) => data.task,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: ['Task'],
    }),
    getTaskComments: builder.query<Comment[], string>({
      query: id => `tasks/${id}/comments`,
      transformResponse: (data: { comments: Comment[] }) => data.comments,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: (_result, _error, id) => [{ type: 'Comment', id: `task-${id}` }],
    }),
    addTaskComment: builder.mutation<Comment, { comment: string; taskId: string }>({
      query: ({ comment, taskId }) => ({
        url: `tasks/${taskId}/comments`,
        method: 'POST',
        body: { comment },
      }),
      transformResponse: (data: { comment: Comment }) => data.comment,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { taskId }) => [{ type: 'Comment', id: `task-${taskId}` }],
    }),
    patchTaskComment: builder.mutation<Comment, { comment: string; id: string; taskId: string }>({
      query: ({ comment, id, taskId }) => ({
        url: `tasks/${taskId}/comments/${id}`,
        method: 'PATCH',
        body: { comment },
      }),
      transformResponse: (data: { comment: Comment }) => data.comment,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { taskId }) => [{ type: 'Comment', id: `task-${taskId}` }],
    }),
    deleteTaskComment: builder.mutation<string, { id: string; taskId: string }>({
      query: ({ id, taskId }) => ({
        url: `tasks/${taskId}/comments/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (data: { commentId: string }) => data.commentId,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { taskId }) => [{ type: 'Comment', id: `task-${taskId}` }],
    }),
    getTaskEvents: builder.query<Event[], string>({
      query: id => `tasks/${id}/events`,
      transformResponse: (data: { events: Event[] }) => data.events,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: (_result, _error, id) => [{ type: 'Event', id }],
    }),
    addTask: builder.mutation<Task, Partial<Task>>({
      query: body => ({
        url: `tasks`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: { task: Task }) => data.task,
      transformErrorResponse: taskApiError,
      invalidatesTags: ['Task'],
    }),
    patchTask: builder.mutation<Task, Partial<Task> & Pick<Task, 'id'>>({
      query: ({ id, ...body }) => ({
        url: `tasks/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (data: { task: Task }) => data.task,
      transformErrorResponse: taskApiError,
      invalidatesTags: (_result, _error, { id }) => ['Task', { type: 'Event', id }],
    }),
    patchManyTasks: builder.mutation<string[], {
      ids: string[];
      infos: {
        assignee?: string | null;
        dueAt?: Date | null;
        projects?: { id: string }[];
      };
    }>({
      query: ({ ids, infos }) => ({
        url: `tasks/batch`,
        method: 'PATCH',
        body: {
          ids,
          infos,
        },
      }),
      transformResponse: (data: { ids: string[] }) => data.ids,
      transformErrorResponse: taskApiError,
      invalidatesTags: (_result, _error, { ids }) => [
        'Task',
        ...ids.map(id => ({ type: 'Event' as const, id })),
      ],
    }),
    updateTask: builder.mutation<Task, Partial<Task> & Pick<Task, 'id'>>({
      query: ({ id, ...body }) => ({
        url: `tasks/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (data: { task: Task }) => data.task,
      transformErrorResponse: taskApiError,
      invalidatesTags: (_result, _error, { id }) => ['Task', { type: 'Event', id }],
    }),
    deleteTask: builder.mutation<string, string>({
      query: id => ({
        url: `tasks/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (data: { taskId: string }) => data.taskId,
      transformErrorResponse: taskApiError,
      invalidatesTags: ['Task'],
    }),
    deleteManyTasks: builder.mutation<string[], { ids: string[]; }>({
      query: ({ ids }) => ({
        url: `tasks/batch`,
        method: 'DELETE',
        body: { ids },
      }),
      transformResponse: (data: { ids: string[] }) => data.ids,
      transformErrorResponse: taskApiError,
      invalidatesTags: ['Task'],
    }),
  }),
});

export const {
  useGetTasksQuery,
  useGetTaskQuery,
  useLazyGetTaskQuery,
  useGetTaskCommentsQuery,
  useLazyGetTaskCommentsQuery,
  useAddTaskCommentMutation,
  usePatchTaskCommentMutation,
  useDeleteTaskCommentMutation,
  useGetTaskEventsQuery,
  useLazyGetTaskEventsQuery,
  useAddTaskMutation,
  usePatchTaskMutation,
  usePatchManyTasksMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useDeleteManyTasksMutation,
} = apiTasks;
